import {
  defineComponent,
  computed,
  onMounted,
  ref,
} from "@vue/composition-api";
import { itemRouteBuilder, NamedRts } from "@/router";

import { Editor, EditorContent } from "tiptap";
import TipTapDisplay from "@/components/TipTapDisplay/TipTapDisplay.vue";
import { Placeholder } from "tiptap-extensions";
import { BLANK_EDITOR_DOCUMENT } from "@/constants";

import { buildSubmitButton } from "@/utilities/form-helpers";
import { friendlyTimeStamp } from "@/utilities/display-helpers";

import { inventoryLotNotesController } from "@/controllers/inventoryLotNotes";
import LoadingAnimation from "@/components/LoadingAnimation/LoadingAnimation.vue";

import { JSConn } from "@/utilities/js-conn";

import { InventoryLot } from "@/models/InventoryLot";
import CircularPercentageChart from "@/components/CircularPercentageChart/CircularPercentageChart.vue";

export default defineComponent({
  name: "inventoryLotDetails",
  props: {
    inventoryLotId: {
      type: String,
      required: true,
    },
  },
  components: {
    CircularPercentageChart,
    LoadingAnimation,
    EditorContent,
    TipTapDisplay,
  },

  setup(props, ctx) {
    let $store = ctx.root.$store;

    let deletingNoteId = ref("");

    let noteContentJSON = ref(BLANK_EDITOR_DOCUMENT);

    let editor = ref<any>(null);

    let { submitButtonText, submitting } = buildSubmitButton();
    submitButtonText.value = "Submit Note";

    const localLoadNotes = () => {
      const jsConnNotes = new JSConn();
      jsConnNotes.formParams = {
        inventory_lot_id: props.inventoryLotId,
      };

      return inventoryLotNotesController
        .needInventoryLotNotes(jsConnNotes)
        .then(jsConn => {
          localNotes.value = jsConn.response;
          return Promise.resolve();
        });
    };

    const deletingNote = (n: { id: string }) => {
      return n.id === deletingNoteId.value;
    };

    const deleteNote = (nt: { id: string }) => {
      deletingNoteId.value = nt.id;
      const jsConn = new JSConn();
      jsConn.formParams = {
        id: nt.id,
      };

      Promise.all([
        inventoryLotNotesController.deleteInventoryLotNote(jsConn),
        // delayController.delay(1000, DelayReturnStatus.resolve),
      ]).then(() => {
        deletingNoteId.value = "";
        localLoadNotes();
      });
    };

    const submitNote = (evt: any) => {
      evt.preventDefault();
      submitting.value = true;
      submitButtonText.value = "Submitting...";

      const jsConn = new JSConn();
      const rightNow = new Date();
      noteContentJSON.value = editor.value.getJSON();

      jsConn.formParams = {
        content_json: noteContentJSON.value,
        inventory_lot_id: props.inventoryLotId,
        created_at: rightNow,
      };

      inventoryLotNotesController.createInventoryLotNote(jsConn).then(() => {
        editor.value.setContent(BLANK_EDITOR_DOCUMENT);
        ctx.root.$nextTick(() => {
          editor.value.focus();
        });

        return localLoadNotes().then(_jsConn => {
          submitting.value = false;
          submitButtonText.value = "Submit Note";
        });
      });

      noteContentJSON.value = BLANK_EDITOR_DOCUMENT;
    };

    const localNotes = ref<any[]>([]);

    const localInventoryLot = computed(() => {
      return $store.getters.inventoryLot as InventoryLot;
    });

    onMounted(() => {
      localLoadNotes();

      editor.value = new Editor({
        extensions: [
          new Placeholder({
            emptyEditorClass: "is-empty",
            emptyNodeText: "Write a note...",
            showOnlyWhenEditable: true,
          }),
        ],
      });

      editor.value.setContent(BLANK_EDITOR_DOCUMENT);
    });

    const editRouteBuilder = (id: string) => {
      return itemRouteBuilder(NamedRts.inventoryLotEditForm, id);
    };

    return {
      localInventoryLot,

      editRouteBuilder,

      editor,
      submitNote,
      submitting,
      submitButtonText,

      localNotes,
      friendlyTimeStamp,
      deleteNote,
      deletingNote,
    };
  },
});
